/**
 * Filtered List (go-gov/filtered-list) — front-end AJAX facet filtering.
 *
 * Reads config from the block wrapper's data-* attributes, posts the selected
 * facet slugs to admin-ajax, and swaps the results + count.
 *
 * Facets are multi-select checkbox dropdowns, the same control the Internship /
 * USAJOBS finder uses. A native <select> had to render the selected term inside
 * the control, so a long one was clipped by the width cap; this toggle shows the
 * facet label plus a count badge and never the term, so length can't overflow.
 */
export default function initFilteredList() {
	const roots = document.querySelectorAll('[data-gfl-root]');
	if (!roots.length) {
		return;
	}

	roots.forEach((root) => {
		const ajaxUrl = root.getAttribute('data-ajax-url');
		const nonce = root.getAttribute('data-nonce');
		const postType = root.getAttribute('data-post-type');
		const perPage = root.getAttribute('data-per-page') || '12';
		// Editor scoping (e.g. Geography = Federal), re-sent on every request so
		// the AJAX re-query stays locked to the same subset as the initial render.
		let prefilters = {};
		try {
			prefilters = JSON.parse(root.getAttribute('data-prefilters') || '{}') || {};
		} catch (e) {
			prefilters = {};
		}
		const facetEls = Array.from(root.querySelectorAll('.filtered-list__facet'));
		const checkboxes = Array.from(root.querySelectorAll('.filtered-list__option-checkbox'));
		const resultsEl = root.querySelector('[data-gfl-results]');
		const countEl = root.querySelector('[data-gfl-count]');
		const pagination = root.querySelector('[data-gfl-pagination]');
		const clearBtn = root.querySelector('[data-gfl-clear]');
		const pillsEl = root.querySelector('[data-gfl-pills]');

		if (!ajaxUrl || !resultsEl) {
			return;
		}

		let requestId = 0;
		let page = 1;

		const getFilters = () => {
			const filters = {};
			checkboxes.forEach((cb) => {
				if (!cb.checked) {
					return;
				}
				const tax = cb.getAttribute('data-taxonomy');
				if (!tax) {
					return;
				}
				(filters[tax] = filters[tax] || []).push(cb.value);
			});
			return filters;
		};

		const anyChecked = () => checkboxes.some((cb) => cb.checked);

		const labelOf = (cb) => {
			const text = cb.closest('.filtered-list__option-label')?.querySelector('.filtered-list__option-text');
			return (text && text.textContent) || cb.value;
		};

		// Count badge on each closed toggle, so a collapsed facet still shows how
		// many of its terms are active.
		const updateToggles = () => {
			facetEls.forEach((facet) => {
				const n = facet.querySelectorAll('.filtered-list__option-checkbox:checked').length;
				const badge = facet.querySelector('[data-gfl-count-badge]');
				if (badge) {
					badge.textContent = String(n);
					badge.hidden = n === 0;
				}
			});
		};

		// Active terms as removable pills + the "Remove all filters" link —
		// same pattern as the USA Jobs Finder (QA round 02 kitchen-sink #5).
		const renderPills = () => {
			updateToggles();
			if (clearBtn) {
				clearBtn.hidden = !anyChecked();
			}
			if (!pillsEl) {
				return;
			}
			pillsEl.innerHTML = '';
			checkboxes.forEach((cb) => {
				if (!cb.checked) {
					return;
				}
				const label = labelOf(cb);
				const pill = document.createElement('button');
				pill.type = 'button';
				pill.className = 'filtered-list__pill';
				pill.setAttribute('data-taxonomy', cb.getAttribute('data-taxonomy') || '');
				pill.setAttribute('data-slug', cb.value);
				pill.setAttribute('aria-label', `Remove filter: ${label}`);
				const text = document.createElement('span');
				text.className = 'filtered-list__pill-label';
				text.textContent = label;
				const x = document.createElement('span');
				x.className = 'filtered-list__pill-x';
				x.setAttribute('aria-hidden', 'true');
				pill.appendChild(text);
				pill.appendChild(x);
				pillsEl.appendChild(pill);
			});
		};

		// Windowed pagination per the design: « ‹ 1 2 3 … 100 › »
		const buildPagination = (totalPages, current) => {
			if (!pagination) {
				return;
			}
			pagination.innerHTML = '';
			if (totalPages <= 1) {
				return;
			}

			const mkBtn = (label, targetPage, opts = {}) => {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.className = 'gfl__page gg-pagination__page';
				if (opts.current) {
					btn.classList.add('--current');
					btn.setAttribute('aria-current', 'page');
				}
				if (opts.disabled) {
					btn.disabled = true;
				}
				if (opts.rel) {
					btn.classList.add(opts.rel);
				}
				if (opts.aria) {
					btn.setAttribute('aria-label', opts.aria);
				}
				btn.textContent = label;
				btn.setAttribute('data-page', targetPage);
				return btn;
			};

			const mkEllipsis = () => {
				const span = document.createElement('span');
				span.className = 'gg-pagination__ellipsis';
				span.setAttribute('aria-hidden', 'true');
				span.textContent = '…';
				return span;
			};

			pagination.appendChild(mkBtn('«', 1, { rel: '--first', disabled: current <= 1, aria: 'First page' }));
			pagination.appendChild(mkBtn('‹', current - 1, { rel: '--prev', disabled: current <= 1, aria: 'Previous page' }));

			// Same window as the USA Jobs Finder: three pages at the head, two at
			// the tail, ±1 around the current page — « ‹ 1 2 3 … 99 100 › ».
			const HEAD = 3;
			const TAIL = 2;
			const AROUND = 1;

			const wanted = new Set();
			for (let i = 1; i <= Math.min(HEAD, totalPages); i += 1) wanted.add(i);
			for (let i = Math.max(1, totalPages - TAIL + 1); i <= totalPages; i += 1) wanted.add(i);
			for (let i = current - AROUND; i <= current + AROUND; i += 1) {
				if (i >= 1 && i <= totalPages) wanted.add(i);
			}

			let previous = 0;
			[...wanted]
				.sort((a, b) => a - b)
				.forEach((p) => {
					if (p - previous > 1) pagination.appendChild(mkEllipsis());
					pagination.appendChild(mkBtn(String(p), p, { current: p === current }));
					previous = p;
				});

			pagination.appendChild(mkBtn('›', current + 1, { rel: '--next', disabled: current >= totalPages, aria: 'Next page' }));
			pagination.appendChild(mkBtn('»', totalPages, { rel: '--last', disabled: current >= totalPages, aria: 'Last page' }));
		};

		/**
		 * @param {Function} [restoreFocus] Runs once the DOM has been rebuilt,
		 *   to hand focus to a replacement for whatever was destroyed.
		 */
		const run = (restoreFocus) => {
			const filters = getFilters();
			renderPills();

			const body = new URLSearchParams();
			body.append('action', 'gg_filtered_list');
			body.append('nonce', nonce || '');
			body.append('postType', postType || '');
			body.append('perPage', perPage);
			body.append('page', String(page));
			Object.keys(filters).forEach((tax) => {
				filters[tax].forEach((slug) => body.append(`filters[${tax}][]`, slug));
			});
			Object.keys(prefilters).forEach((tax) => {
				body.append(`prefilters[${tax}]`, prefilters[tax]);
			});

			const thisRequest = ++requestId;
			root.classList.add('is-loading');

			fetch(ajaxUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: body.toString(),
			})
				.then((res) => res.json())
				.then((json) => {
					// Ignore out-of-order responses.
					if (thisRequest !== requestId) {
						return;
					}
					root.classList.remove('is-loading');
					if (!json || !json.success || !json.data) {
						return;
					}
					resultsEl.innerHTML = json.data.cards;
					if (countEl) {
						const n = parseInt(json.data.total, 10) || 0;
						countEl.textContent = `${n} ${n === 1 ? 'Result' : 'Results'}`;
					}
					buildPagination(parseInt(json.data.pages, 10) || 0, page);
					if (typeof restoreFocus === 'function') {
						restoreFocus();
					}
				})
				.catch(() => {
					if (thisRequest === requestId) {
						root.classList.remove('is-loading');
					}
				});
		};

		const closeAll = (except) => {
			facetEls.forEach((facet) => {
				if (facet === except) {
					return;
				}
				facet.classList.remove('--open');
				facet.querySelector('[data-gfl-dropdown]')?.setAttribute('hidden', '');
				facet.querySelector('[data-gfl-toggle]')?.setAttribute('aria-expanded', 'false');
			});
		};

		facetEls.forEach((facet) => {
			const toggle = facet.querySelector('[data-gfl-toggle]');
			const dropdown = facet.querySelector('[data-gfl-dropdown]');
			const search = facet.querySelector('[data-gfl-search]');
			const options = Array.from(facet.querySelectorAll('[data-gfl-option]'));

			if (toggle && dropdown) {
				toggle.addEventListener('click', () => {
					const isOpen = !dropdown.hasAttribute('hidden');
					closeAll(facet);
					if (isOpen) {
						dropdown.setAttribute('hidden', '');
						facet.classList.remove('--open');
						toggle.setAttribute('aria-expanded', 'false');
					} else {
						dropdown.removeAttribute('hidden');
						facet.classList.add('--open');
						toggle.setAttribute('aria-expanded', 'true');
						search?.focus();
					}
				});
			}

			// Client-side substring match over the already-rendered options.
			if (search) {
				search.addEventListener('input', () => {
					const q = search.value.trim().toLowerCase();
					options.forEach((opt) => {
						const label = opt.getAttribute('data-label') || '';
						opt.hidden = q !== '' && label.indexOf(q) === -1;
					});
				});
			}
		});

		document.addEventListener('click', (e) => {
			if (!root.contains(e.target) || !e.target.closest('.filtered-list__facet')) {
				closeAll(null);
			}
		});
		document.addEventListener('keydown', (e) => {
			if ('Escape' === e.key) {
				closeAll(null);
			}
		});

		checkboxes.forEach((cb) =>
			cb.addEventListener('change', () => {
				page = 1;
				run();
			})
		);

		// Pagination (delegated). Rebuilding the strip destroys the button that
		// was clicked, so without a handoff focus falls to <body> — and
		// scrollIntoView has already moved the viewport away from it.
		if (pagination) {
			pagination.addEventListener('click', (e) => {
				const btn = e.target.closest('.gfl__page');
				if (!btn || btn.disabled) {
					return;
				}
				const target = parseInt(btn.getAttribute('data-page'), 10);
				if (!target || target === page) {
					return;
				}
				page = target;
				run(() => {
					const same = pagination.querySelector(
						`.gfl__page[data-page="${target}"]:not([disabled])`
					);
					(same || pagination.querySelector('.gfl__page:not([disabled])'))?.focus();
				});
				root.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
		}

		// Clear all. The button hides itself once the filters are gone, so move
		// focus to the first facet before it disappears from under the visitor.
		if (clearBtn) {
			clearBtn.addEventListener('click', () => {
				checkboxes.forEach((cb) => {
					cb.checked = false;
				});
				page = 1;
				facetEls[0]?.querySelector('[data-gfl-toggle]')?.focus();
				run();
			});
		}

		// Remove a single term via its pill. renderPills() wipes the pill that was
		// clicked, so hand focus to the facet it belongs to first.
		if (pillsEl) {
			pillsEl.addEventListener('click', (e) => {
				const pill = e.target.closest('.filtered-list__pill');
				if (!pill) {
					return;
				}
				const tax = pill.getAttribute('data-taxonomy');
				const slug = pill.getAttribute('data-slug');
				const cb = checkboxes.find(
					(c) => c.getAttribute('data-taxonomy') === tax && c.value === slug
				);
				if (cb) {
					cb.checked = false;
					cb.closest('.filtered-list__facet')?.querySelector('[data-gfl-toggle]')?.focus();
				}
				page = 1;
				run();
			});
		}

		// Build initial pagination from the server-rendered state.
		buildPagination(
			parseInt(pagination?.getAttribute('data-total-pages'), 10) || 0,
			parseInt(pagination?.getAttribute('data-current'), 10) || 1
		);
	});
}
